import ComposeItem             from "./Entities/ComposeItem";
import ComposeHTTPService      from "./HTTP/ComposeHTTPService";
import ComposeHTTPMapping      from "./HTTP/ComposeHTTPMapping";
import StateEventManager       from "@/js/Shared/Events/Managers/StateEventManager";
import EventUpload             from "@/js/Shared/Events/EventUpload";
import FilesEvent              from "@/js/Shared/Events/Entities/FilesEvent";
import BroadcastEventManager   from "@/js/Shared/Events/Managers/BroadcastEventManager";
import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import Language                from "@/js/Shared/Language/Language";
import SAPOMailConfig          from "@/SAPOMailConfig";
import FoldersController       from "@/js/Mailboxes/FoldersController";
import MessageAttachmentsItem  from "@/js/Mailboxes/Entities/Message/MessageAttachmentsItem";
import AccountController       from "../Webmail/AccountController";
import MailboxController       from "@/js/Mailboxes/MailboxController";

export {ComposeController as default};

/**
* HouseKeeper Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ComposeController {
  /**
   * Defines all fetch message method
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {ComposeItem}
   */
  FETCH_MESSAGE_METHODS = {
    'draft'              : 'fetchMessageDraft',
    'reply'              : 'fetchMessageReply',
    'reply_all'          : 'fetchMessageReplyAll',
    'forward'            : 'fetchMessageForward',
    'forward_new'        : 'fetchMessageForwardAsNew',
    'forward_attachment' : 'fetchMessageForwardAsAttachment'
  }

  /**
   * Compose Item
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {ComposeItem}
   */
  compose = new ComposeItem();

  /**
   * Compose Event State
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {StateEventManager}
   */
  state = new StateEventManager();

  /**
   * Event Broadcaster
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {BroadcastEventManager}
   */
  broadcaster = new BroadcastEventManager();

  /**
   * Fetches message data by a given type
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   * @param {String} type       reply type, reply_auto|reply_all|reply
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageByType(message_id, folder_id, type) {
    if (this.FETCH_MESSAGE_METHODS[type]) {
      return await this[this.FETCH_MESSAGE_METHODS[type]](message_id, folder_id);
    }

    this.state.setSuccess('fetch_message', true);
    return true;
  }

  /**
   * Fetches forward message from server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageForward(message_id, folder_id) {
    return this.fetchMessageForwardData(message_id, folder_id, 'forward_auto');
  }

  /**
   * Fetches forward as new message from server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageForwardAsNew(message_id, folder_id) {
    return this.fetchMessageForwardData(message_id, folder_id, 'editasnew');
  }

  /**
   * Fetches forward as message attachment from server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageForwardAsAttachment(message_id, folder_id) {
    return this.fetchMessageForwardData(message_id, folder_id, 'forward_attach');
  }

  /**
   * Fetches forward message data from server
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   * @param {String} type       reply type, reply_auto|reply_all|reply
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageForwardData(message_id, folder_id, type) {
    this.state.setLoading('fetch_message');
    let response = await ComposeHTTPService.fetchFowardData(message_id, folder_id, type);
    this.handleResponse(response);
    this.state.setSuccess('fetch_message', response.success);
    return response.success;
  }

  /**
   * Fetches forward message data from server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageDraft(message_id, folder_id) {
    this.state.setLoading('fetch_message');
    let response = await ComposeHTTPService.fetchDraftData(message_id, folder_id);
    this.handleResponse(response);
    this.state.setSuccess('fetch_message', response.success);
    return response.success;
  }

  /**
   * Fetches reply message from server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageReply(message_id, folder_id) {
    return this.fetchMessageReplyData(
      message_id,
      folder_id,
      (folder_id === SAPOMailConfig.client.folders.alias.sent) ? 'reply_auto' : 'reply',
    )
  }

  /**
   * Fetches reply to all message from server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageReplyAll(message_id, folder_id) {
    return this.fetchMessageReplyData(
      message_id,
      folder_id,
      (folder_id === SAPOMailConfig.client.folders.alias.sent) ? 'reply_auto' : 'reply_all',
    )
  }

  /**
   * Fetches reply message data from server
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   * @param {String} type       reply type, reply_auto|reply_all|reply
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessageReplyData(message_id, folder_id, type) {
    this.state.setLoading('fetch_message');
    let response = await ComposeHTTPService.fetchReplyData(message_id, folder_id, type);
    this.handleResponse(response);
    this.state.setSuccess('fetch_message', response.success);
    return response.success;
  }

  /**
   * Sends a composer message to the server
   *
   * Validates all composer params to make sure that
   * the that beeing sent is valid
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async saveDraft() {
    this.state.setLoading('send_message');
    let response = await ComposeHTTPService.saveDraft(this.compose);

    // We do not care if this fail, we just need it to happen before the notification
    if (response.success === true) {
      await FoldersController.poll(
        SAPOMailConfig.client.folders.alias.drafts,
      )
    }

    this.handleMessages(response);
    this.state.setSuccess('send_message', response.success);

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.compose_save_draft_success');
    }

    return response.success;
  }

  /**
   * Sends a composer message to the server
   *
   * Validates all composer params to make sure that
   * the that beeing sent is valid
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async saveAutoDraft() {
    let response = await ComposeHTTPService.saveAutoDraft(this.compose);
    this.handleMessages(response);

    if (response.success === true) {
      await FoldersController.poll(
        SAPOMailConfig.client.folders.alias.drafts,
      )
    }

    return response.success;
  }

  /**
   * Sends a composer message to the server
   *
   * Validates all composer params to make sure that
   * the that beeing sent is valid
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async sendMessage() {
    if (this.validateCompose() === false) {
      return false;
    }

    this.state.setLoading('send_message');
    let response = await ComposeHTTPService.sendMessage(this.compose);

    if (response.success === true && this.compose.type === 'draft') {
      FoldersController.poll(
        SAPOMailConfig.client.folders.alias.drafts,
      )
    }

    this.handleResponse(response);
    this.state.setSuccess('send_message', response.success);

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.compose_send_success');
    }

    return response.success;
  }

  /**
   * Calls the server to store all the inline images as an attachment
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent} images
   *
   * @return {Promise<void>}
   */
  async addInlineImages(images) {
    if (this.validateAttachmentsLimit(images.size, images.items.length) === false) {
      return false;
    }

    images.items.forEach(image => {
      let event    = new EventUpload();
      event.id     = image.id;
      event.file   = image;
      event.type   = 'inline';
      event.target = 'internal';
      event.id     = image.id;

      this.uploadAttachments(event, 'upload-attachment-inline-finished');
    });
  }

  /**
   * Creates a new event upload for each attachment and uploads it to the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent} files
   *
   * @return {Promise<void>}
   */
  async addAttachments(files) {
    if (this.validateAttachmentsLimit(files.size, files.items.length) === false) {
      return false;
    }

    files.items.forEach(file => {
      let event    = new EventUpload();
      event.id     = file.id;
      event.file   = file;
      event.type   = 'attachment';
      event.target = 'internal';
      event.id     = file.id;

      this.uploadAttachments(event);
    });
  }

  /**
   * Calls the server to upload an attachment
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {EventUpload} event
   *
   * @return {Promise<void>}
   */
  async uploadAttachments(event, broadcast_message = 'upload-attachment-finished') {
    event.broadcaster.on('upload-finished', (response) => {
      this.handleResponse(response);
      this.compose.attachments_upload.delete(event.id);

      if (response.success === false) {
        return;
      }

      this.broadcaster.broadcast(broadcast_message, this.compose.attachments_inline.get(event.id));
    })

    this.compose.attachments_upload.add(event, event.id);
    ComposeHTTPService.addAttachment(this.compose.id, event);
  }

  /**
   * Creates a meo cloud attachment item
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} files  meo cloud files
   *
   * @return {Promise<MessageAttachmentsItem>}
   */
  async addMeoCloudAsAttachment(files) {
    let attachment = new MessageAttachmentsItem();
    attachment.id         = files.id;
    attachment.target     = 'meo-cloud';
    attachment.related_id = files.id;
    attachment.name       = (files.count === 1)
                          ? files.public_links[0].filename + ' ' + Language.say('composer.meocloud.file_via')
                          : Language.say('composer.meocloud.files_name', [files.count]);

    this.compose.attachments.add(attachment, attachment.id);
    return attachment;
  }

  /**
   * Calls the correct delete attachment method by the defined
   * attachment target
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessageAttachmentsItem} attachment
   *
   * @return {Promise<Boolean>}
   */
  async deleteAttachmentsByTarget(attachment) {
    if (attachment.target === 'internal') {
      return await this.deleteAttachments(attachment);
    }

    this.deleteSharedFilesAttachments(attachment);
  }

  /**
   * Calls the correct abort upload method by the defined
   * upload target
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {EventUpload} upload
   *
   * @return {Promise<Boolean>}
   */
  async abortUploadByTarget(upload) {
    upload.xhr.abort();
    this.compose.attachments_upload.delete(upload.id);
    this.broadcaster.broadcast(`file-share-upload-aborted`, upload.target_id);
  }

  /**
   * Calls the server to delete an attachment uploaded
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessageAttachmentsItem} attachment
   *
   * @return {Promise<Boolean>}
   */
  async deleteAttachments(attachment) {
    this.state.set(`attach_id_${attachment.id}`, 'loading');
    let response = await ComposeHTTPService.deleteAttachment(this.compose.id, attachment.id);
    this.handleResponse(response);
    this.state.unset(`attach_id_${attachment.id}`);
    return response.success;
  }

  /**
   * Creates a new event upload for each attachment and uploads it to the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent} files
   *
   * @return {Promise<Boolean>}
   */
  async addSapoTransferFiles(files) {
    if (this.validateSapoTransferLimit(files) === false) {
      return false;
    }

    let transfer_data = await ComposeHTTPService.getSapoTransferUploadData(files);

    if (transfer_data.success === false) {
      NotificationsController.addMessage('notifications.', transfer_data.messages);
      return false;
    }

    if (await this.uploadSapoTransferFiles(files, transfer_data.results) === false) {
      NotificationsController.addError('notifications.sapo_transfer_upload_error');
      return false;
    }

    this.compose.attachments_upload.add(
      this.compose.attachments_upload.last,
      this.compose.attachments_upload.last.id
    );

    let sign_data  = await ComposeHTTPService.signSapoTransferFile(transfer_data.results, files);

    if (sign_data.success === false) {
      NotificationsController.addMessage('notifications.', sign_data.messages);
      this.compose.attachments_upload.delete(
        this.compose.attachments_upload.last.id
      );

      this.broadcaster.broadcast('file-share-upload-aborted', this.compose.attachments_upload.last.target_id);
      return false;
    }

    let attachment = ComposeHTTPMapping.mapComposeTransferAttachment(this.compose, files, transfer_data.results, sign_data.results);

    this.compose.attachments_upload.delete(
      this.compose.attachments_upload.last.id
    );

    if (attachment.files_count === 0) {
      this.deleteSharedFilesAttachments(attachment);
      return false;
    }

    this.broadcaster.broadcast('file-share-upload-finished', {
      attachment: attachment,
      metadata  : sign_data.results,
    });
    return true;
  }

  /**
   * Uploads all Sapo Transfer files via queue
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array<EventUpload>} files
   * @param {Object}             transfer_data transfer data
   *
   * @return {Promise<void>}
   */
  async uploadSapoTransferFiles(files, transfer_data) {
    let data = {
      id       : transfer_data.id,
      target   : 'sapo-transfer',
      events   : [],
      current  : 0,
      total    : files.items.length,
      finished : false,
    };

    files.items.forEach(file => {
      let event        = new EventUpload();
      event.id         = file.id;
      event.file       = file;
      event.type       = 'attachment';
      event.target     = 'sapo-transfer';
      event.target_id  = transfer_data.id;
      event.upload_url = transfer_data.data.upload_files[file.name];
      data.events.push(event);

      event.broadcaster.on('upload-progress', (e) => {
        this.broadcaster.broadcast('file-share-upload-progress', data);
      })
    });

    this.broadcaster.broadcast('file-share-upload-started', data);

    try {
      for (data.current; data.current < data.total; data.current++) {

        await this.uploadSapoTransferFile(data.events[data.current], transfer_data);
        this.compose.attachments_upload.delete(data.events[data.current].id);
      }

      return true;
    } catch (error) {
      this.compose.attachments_upload.delete(data.events[data.current].id);
      this.broadcaster.broadcast('file-share-upload-aborted', data.id);
      return false;
    }
  }

  /**
   * Uploads a single file to sapo transfer
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array<EventUpload>} files
   * @param {Object}             transfer transfer data
   *
   * @return {Promise<void>}
   */
  async uploadSapoTransferFile(event, transfer) {
    return new Promise((resolve, reject) => {
      event.broadcaster.on('upload-finished', (response) => {
        if (response.success === false) {
          return reject();
        }

        return resolve();
      })

      ComposeHTTPService.addSapoTransferFile(event, transfer);
      this.compose.attachments_upload.add(event, event.id);
    })
  }

  /**
   * Deletes Sapo Transfer Attachments
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessageAttachmentsItem} attachment
   *
   * @return {Promise<Boolean>}
   */
  async deleteSharedFilesAttachments(attachment) {
    this.compose.attachments.delete(attachment.id);
    this.broadcaster.broadcast('file-share-upload-aborted', attachment.id);
    return true;
  }

  /**
   * Validates Attachments limits
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} size  size of attachments being added
   * @param {Number} count number of attachments beeing added
   *
   * @return {Boolean}
   */
  validateAttachmentsLimit(size, count) {
    if (this.compose.attachments_size + size > SAPOMailConfig.client.compose.attachments.size_limit) {
      NotificationsController.addError('notifications.compose_attachments_size_error');
      return false;
    }

    if (this.compose.attachments_total + count > SAPOMailConfig.client.compose.attachments.file_limit) {
      NotificationsController.addError('notifications.compose_attachments_limit_error');
      return false;
    }

    return true;
  }

  /**
   * Validates Sapo Transfer limits
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent} files
   *
   * @return {Boolean}
   */
  validateSapoTransferLimit(files) {
    if (files.size > SAPOMailConfig.client.compose.sapo_transfer.size_limit) {
      NotificationsController.addError('notifications.compose_sapo_transfer_size_error');
      return false;
    }

    return true;
  }

  /**
   * Validates all compose fields
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  validateCompose() {
    if (this.validateComposeRecipientsMinLimit() === false) {
      NotificationsController.addError('notifications.compose_recipients_min_limit_error');
      return false;
    }

    if (this.validateComposeRecipientsMaxLimit() === false) {
      NotificationsController.addError('notifications.compose_recipients_max_limit_error', [AccountController.account.cos.max_rcpt]);
      return false;
    }

    if (this.validateComposeRecipientsEmails() === false) {
      NotificationsController.addError('notifications.compose_recipients_email_error');
      return false;
    }
  }

  /**
   * Validates compose recipients emails
   *
   * Checks if all recipients have a valid email
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  validateComposeRecipientsEmails() {
    if (this.compose.to.validEmails() === false) {
      return false;
    }

    if (this.compose.cc.validEmails() === false) {
      return false;
    }

    if (this.compose.bcc.validEmails() === false) {
      return false;
    }

    return true;
  }

  /**
   * Validates compose maximum recipients limit
   *
   * Checks if the number of recipients exceeds the allowed limit
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  validateComposeRecipientsMaxLimit() {
    let count = this.compose.bcc.count + this.compose.cc.count + this.compose.to.count;
    return (count < AccountController.account.cos.max_rcpt);
  }

  /**
   * Validates compose minium recipients limit
   *
   * Checks if the number of recipients is whithin the allowed limit
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  validateComposeRecipientsMinLimit() {
    let count = this.compose.bcc.count + this.compose.cc.count + this.compose.to.count;
    return (count > 0);
  }

  /**
   * Handles horde response
   *
   * Processes horde tasks and messages
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTTPResponseEntity} response response data
   *
   * @return {Promise.<void>}
   */
  handleResponse(response) {
    if (response.messages) {
      this.handleMessages(response);
    }

    if (response.results) {
      this.handleTasks(response.results);
    }
  }

  /**
   * Handles all horde tasks
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} results results data
   *
   * @return {Promise<void>}
   */
  handleTasks(results) {
    if (results['imp:compose']) {
      ComposeHTTPMapping.mapCompose(this.compose, results['imp:compose']);
    }

    if (results['imp:compose-atc']) {
      ComposeHTTPMapping.mapComposeAttachment(this.compose, results['imp:compose-atc']);
    }

    if (results['imp:compose-data']) {
      ComposeHTTPMapping.mapComposeData(this.compose, results['imp:compose-data']);
    }

    if (results['imp:deleteAttachment']) {
      ComposeHTTPMapping.mapDeleteAttachment(this.compose, results['imp:deleteAttachment']);
    }

    if (results['imp:addAttachment']) {
      ComposeHTTPMapping.mapAddAttachment(this.compose, results['imp:addAttachment']);
    }

    if (results['imp:flag']) {
      MailboxController.viewport_mapping.mapFlags(MailboxController.viewport, results['imp:flag']);
    }
  }

  /**
   * Handles horder server messages
   *
   * Display notification notices with service messages
   *
   * @note Message handling for horde requests is very inconsistent,
   *       sometimes the server returns a message, sometimes it doesn't,
   *       as of now we ignore everything that is not an error and handle success messages in the client itself
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTTPResponseEntity} response response data
   *
   * @return {Promise.<void>}
   */
  handleMessages(response) {
    let type = {
      'horde.warning' : 'addWarning',
      'horde.error'   : 'addError',
    };

    response.messages.forEach((message) => {
      if (!type[message.type]) {
        return;
      }

      NotificationsController[type[message.type]](message.message);
      response.success = false;
    })
  }
}
